/**
 * Legal document page (audit P-4, 2026-10-22). Renders the reviewed markdown in
 * public/legal/{terms,privacy}.md as safe React elements. The markdown content
 * itself is owned by another workstream (counsel review); this only fetches and
 * renders it. No raw HTML is ever rendered.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Paper,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import SafeMarkdown from './SafeMarkdown';

const DOCS = {
  terms: { file: 'terms.md', label: 'Terms of Service' },
  privacy: { file: 'privacy.md', label: 'Privacy Policy' },
};

const MAX_BYTES = 512 * 1024; // A legal doc far exceeding this is treated as bad.
const FETCH_TIMEOUT_MS = 15000;

/**
 * @param {{ kind: 'terms'|'privacy', onBack?: () => void }} props
 */
export default function LegalPage({ kind, onBack }) {
  const doc = DOCS[kind];
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!doc) {
      setError('Unknown document.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const base = process.env.PUBLIC_URL || '';
      const res = await fetch(`${base}/legal/${doc.file}`, { signal: controller.signal });
      if (!res.ok) {
        setError('This document is unavailable right now.');
        return;
      }
      const text = await res.text();
      if (typeof text !== 'string' || text.length > MAX_BYTES) {
        setError('This document is unavailable right now.');
        return;
      }
      setContent(text);
    } catch {
      setError('This document could not be loaded. Check your connection and retry.');
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  }, [doc]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', py: 2 }}>
      {onBack && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          Back
        </Button>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {!loading && error && (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
        >
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2 }} className="il-legal-doc">
          <SafeMarkdown content={content} />
        </Paper>
      )}
    </Box>
  );
}
